import { useEffect, useRef, useState } from "react";
import { ChevronDown, Globe } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";

/**
 * Compact currency switcher for the nav bar.
 * Shows the active currency code (e.g. "USD"), a globe icon, and a dropdown
 * of supported currencies sourced live from the CurrencyContext.
 */
export default function CurrencyPicker() {
  const { currency, supported, setCurrency, source, name } = useCurrency();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!supported?.length) return null;

  return (
    <div ref={wrapRef} className="relative" data-testid="currency-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] transition-colors px-2 py-1.5"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="currency-picker-toggle"
        title={`Display currency: ${name} (${source})`}
      >
        <Globe className="w-4 h-4" />
        <span className="font-mono text-xs">{currency}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-1 w-56 max-h-72 overflow-auto bg-white border border-[var(--rl-border)] shadow-lg z-50"
          data-testid="currency-picker-dropdown"
        >
          {supported.map((c) => {
            const active = c.code === currency;
            return (
              <li key={c.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => { setCurrency(c.code); setOpen(false); }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm transition-colors ${active ? "bg-[var(--rl-brand)] text-white" : "hover:bg-[var(--rl-bg-subtle)]"}`}
                  data-testid={`currency-option-${c.code}`}
                >
                  <span className="flex items-center gap-2">
                    <span className="font-mono text-xs w-12 text-left">{c.code}</span>
                    <span className="text-left">{c.name}</span>
                  </span>
                  <span className="opacity-70">{c.symbol}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
